<script setup lang="ts">
import type { CartItemType, ProductType } from "@/types";
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from "vue";
import dayjs from "dayjs";
import { formatterUtils } from "@/utils/formatterUtils";
import RawHtmlRenderer from "~/components/Common/RawHtmlRenderer.vue";
import OnePassPerks from "~/components/Product/OnePassPerks.vue";
import MarkdownIt from "markdown-it";

const formatter = formatterUtils();
const { settings: globalSettings } = useGlobalSettings();
const md = new MarkdownIt({ html: true, linkify: true, breaks: true });

// ─── FAQs ───
const { faqs, fetchFaqs } = useFaqs();
const productFaqSlugs = ['product-faq-free-membership', 'product-faq-5x-Flybuys'];
const expandedFaqs = ref<Record<string, boolean>>({});

const productFaqs = computed(() => {
  if (!faqs.value) return [];
  return productFaqSlugs
    .map(slug => faqs.value!.find(f => f.slug === slug))
    .filter((f): f is NonNullable<typeof f> => !!f);
});

const toggleFaq = (slug: string) => {
  expandedFaqs.value[slug] = !expandedFaqs.value[slug];
};

const renderFaqContent = (content: string) => {
  if (!content) return '';
  return md.render(content);
};

// Extend the StrapiProductType interface
declare module '@/types' {
  interface StrapiProductType {
    detailedPromotionDescription?: string;
  }
}

declare global {
  interface GOCommerceCustomFields {
    metadataDetailedPromotionDescription?: string;
    metadataBestValue?: string;
    metadataFeatureText?: string;
    isFeaturedProduct?: boolean;
    quotaDataGb?: number;
    validityDuration?: number;
  }
  interface DiscountPlanItem {
    customFields?: any;
    [key: string]: any;
  }
}

type PropsType = {
  product: ProductType;
  cartItem: CartItemType;
  handlePosition: string;
  updateHandlePosition: () => void;
  totalItemsInCart: number;
  updateQuantity: (action: string) => void;
  addToCart: () => void;
  buyNow: () => void;
  clearFields: () => void;
};

const props = defineProps<PropsType>();
const { currency, getProductPrice, getProductDiscountValue } = formatter;

// ─── Computed: custom fields shortcut ───
const customFields = computed(() => {
  return props.product?.offerServiceTemplate?.[0]?.serviceTemplate?.customFields;
});

// ─── Computed: Pricing ───
const finalPrice = computed(() => getFinalPrice(props.product));
const originalPrice = computed(() => getOriginalPrice(props.product));
const hasDiscount = computed(() => hasProductDiscount(props.product));
const quotaDataGb = computed(() => getQuotaDataGb(props.product));
// IGNITE: the allowance the customer reverts to once the promotion ends, i.e.
// without any bonus data. Only the ongoing-recharge line uses it; the headline
// and the sticky bar quote the promotional allowance alongside the promotional
// price, which is correct.
const baseQuotaDataGb = computed(() => getBaseQuotaDataGb(props.product));

/**
 * IGNITE-119: hasDiscount only means a monetary discount. A bonus-data-only
 * plan leaves the price alone and only lifts the allowance, so the promo badge
 * and the ongoing statement both disappeared on those products even though Ops
 * had filled promotionFlagText in.
 *
 * The ongoing statement matters most here: with bonus data the headline shows
 * the promotional allowance, so without this line nothing tells the customer
 * what they revert to.
 */
const hasPromotion = computed(
  () => hasDiscount.value || getBonusDataGbNumber(props.product) > 0
);
const planDurationText = computed(() => getPlanDurationText(props.product));
const planDurationDays = computed(() => getRenewalDuration(props.product));

// ─── Computed: Plan info ───
const planName = computed(() => {
  return props.product?.name || "";
});

const planDescription = computed(() => {
  return (customFields.value?.pim?.goCommerce as any)?.byoNumber ||
    '';
});

// ─── Computed: Promo badge ───
const promoBadgeText = computed(() => {
  if (!hasPromotion.value) return "";

  // Use promotionFlagText from backend if available
  const promotionFlagText = (customFields.value?.pim?.goCommerce as any)?.promotionFlagText;
  if (promotionFlagText) return promotionFlagText;

  const priceObj = getRecurringPriceObject(props.product);
  const discount = priceObj?.discount;
  const duration = discount?.defaultDuration || 0;
  const itemType = discount?.discountPlanItemType || "";

  if (itemType === "PERCENTAGE" && discount?.discountValue) {
    const pct = Math.abs(Number(discount.discountValue));
    if (duration > 1) {
      return `Save ${pct}% off on first ${duration} recharges`;
    }
    return `Save ${pct}% off on first recharge`;
  }

  const amount = getDiscountAmount(props.product);
  if (duration > 1) {
    return `Save $${amount} on first ${duration} recharges`;
  }
  return `Save $${amount} on first recharge`;
});

// ─── Parsed promotion HTML (benefits + T&C from backend) ───
const parsedPromotionHTML = ref("");

const detailedPromotionDescription = computed(() => {

  return props.product?.offerServiceTemplate?.[0]?.serviceTemplate?.customFields?.pim
    ?.goCommerce?.metadataDetailedPromotionDescription || "";
});

onMounted(() => {
  updatePromotionHTML();
  fetchFaqs();
});

watch(detailedPromotionDescription, () => {
  updatePromotionHTML();
});

// ─── Refs ───
const buyNowRef = ref<HTMLElement | null>(null);
const showStickyBar = ref(false);
const pdpColumnsRef = ref<HTMLElement | null>(null);
const pdpRightRef = ref<HTMLElement | null>(null);

// ─── JS-based sticky (CSS sticky broken by .page-container overflow:auto) ───
function getScrollParent(el: HTMLElement | null): HTMLElement {
  let node = el?.parentElement;
  while (node && node !== document.documentElement) {
    if (node.scrollTop > 0) return node;
    // Try scrolling 1px to detect
    node.scrollTop = 1;
    if (node.scrollTop > 0) {
      node.scrollTop = 0;
      return node;
    }
    node = node.parentElement;
  }
  return document.body;
}

function onScroll() {
  // Sticky OnePass card
  if (pdpColumnsRef.value && pdpRightRef.value && window.innerWidth > 768) {
    const container = pdpColumnsRef.value;
    const card = pdpRightRef.value;
    const containerRect = container.getBoundingClientRect();
    const stickyTop = 32;
    const maxOffset = Math.max(0, container.offsetHeight - card.offsetHeight);

    if (containerRect.top >= stickyTop) {
      card.style.transform = 'translateY(0)';
    } else {
      const offset = Math.min(stickyTop - containerRect.top, maxOffset);
      card.style.transform = `translateY(${offset}px)`;
    }
  }

  // Sticky bottom bar
  if (buyNowRef.value && window.innerWidth > 768) {
    const rect = buyNowRef.value.getBoundingClientRect();
    showStickyBar.value = rect.bottom < 0;
  }
}

let scrollParent: HTMLElement | null = null;

onMounted(() => {
  nextTick(() => {
    scrollParent = getScrollParent(pdpColumnsRef.value);
    scrollParent.addEventListener('scroll', onScroll, { passive: true });
    // Initial check
    onScroll();
  });
});

onUnmounted(() => {
  if (scrollParent) {
    scrollParent.removeEventListener('scroll', onScroll);
  }
});

// ─── Modal states ───
const showOpModal = ref(false);
const showFlybuysModal = ref(false);
const showSpeedModal = ref(false);

const opModalDescription = computed(() => {
  return (customFields.value?.pim?.goCommerce as any)?.opModalDescription || "";
});
const flybuysModalDescription = computed(() => {
  return (customFields.value?.pim?.goCommerce as any)?.flybuysDescription || "";
});
const speedModalDescription = computed(() => {
  return (customFields.value?.pim?.goCommerce as any)?.networkSpeedDescription || "";
});

// ─── Event delegation for data-modal links in rendered HTML ───
const handleBenefitsClick = (event: Event) => {
  const target = event.target as HTMLElement;
  const modalTrigger = target.closest("[data-modal]") as HTMLElement | null;
  if (modalTrigger) {
    event.preventDefault();
    const modalType = modalTrigger.getAttribute("data-modal");
    if (modalType === "op-modal") showOpModal.value = true;
    else if (modalType === "flybuys-modal") showFlybuysModal.value = true;
    else if (modalType === "speed-modal") showSpeedModal.value = true;
  }
};

// ─── HTML placeholder processing (from Modern.vue) ───
function updatePromotionHTML() {
  try {
    if (detailedPromotionDescription.value && detailedPromotionDescription.value.trim()) {
      let htmlContent = detailedPromotionDescription.value;
      const placeholderRegex = /\{\{([^{}]+)\}\}/g;
      const placeholders = htmlContent.match(placeholderRegex) || [];

      for (const placeholder of placeholders) {
        const path = placeholder.slice(2, -2).trim();
        let replacementValue = '';

        switch (path) {
          case 'quotaDataGb':
            replacementValue = String(getQuotaDataGb(props.product));
            break;
          case 'baseQuotaDataGb':
            replacementValue = String(getBaseQuotaDataGb(props.product));
            break;
          case 'bonusDataGbNumber':
            replacementValue = String(getBonusDataGbNumber(props.product));
            break;
          case 'bonusDataAmount':
            replacementValue = getBonusDataAmount(props.product);
            break;
          case 'finalPrice':
            replacementValue = getFinalPrice(props.product);
            break;
          case 'discountAmount':
            replacementValue = getDiscountAmount(props.product);
            break;
          case 'hasDiscount':
            replacementValue = String(hasProductDiscount(props.product));
            break;
          case 'originalPrice':
            replacementValue = getOriginalPrice(props.product);
            break;
          case 'discountedPrice':
            replacementValue = getDiscountedPrice(props.product);
            break;
          case 'rppPrice':
            replacementValue = getRppPrice(props.product);
            break;
          case 'planDurationText':
            replacementValue = getPlanDurationText(props.product);
            break;
          case 'durationUnit':
            replacementValue = getDurationUnit(props.product);
            break;
          case 'renewalDuration':
            replacementValue = getRenewalDuration(props.product);
            break;
          case 'isBestValue':
            replacementValue = isProductBestValue(props.product);
            break;
          case 'featureText':
            replacementValue = getFeatureText(props.product);
            break;
          case 'discountEndDate': {
            const endDate = getDiscountDetails(props.product)?.endDate;
            replacementValue = endDate ? dayjs(endDate).format('DD/MM/YYYY') : '';
            break;
          }
          case 'discountEndDateLong': {
            const endDateLong = getDiscountDetails(props.product)?.endDate;
            replacementValue = endDateLong ? dayjs(endDateLong).format('D MMMM YYYY') : '';
            break;
          }
          case 'discountStartDate': {
            const startDate = getDiscountDetails(props.product)?.startDate;
            replacementValue = startDate ? dayjs(startDate).format('DD/MM/YYYY') : '';
            break;
          }
          case 'discountEndDateShort': {
            const endDateShort = getDiscountDetails(props.product)?.endDate;
            replacementValue = endDateShort ? dayjs(endDateShort).format('DD MMM') : '';
            break;
          }
          case 'discountEndDateLongWithYear': {
            const endDateLongWithYear = getDiscountDetails(props.product)?.endDate;
            replacementValue = endDateLongWithYear ? dayjs(endDateLongWithYear).format('D MMMM YYYY') : '';
            break;
          }
        }

        if (path.includes('dayjs') && path.includes('.format')) {
          replacementValue = 'Format not supported';
        }

        if (replacementValue === '') {
          try {
            const value = getValueFromPath(props.product, path);
            if (value !== undefined) {
              replacementValue = value.toString();
            }
          } catch (err) {
            console.error(`Error accessing path ${path}:`, err);
          }
        }

        htmlContent = htmlContent.replace(placeholder, replacementValue);
      }

      // Strip inline width:70% from backend wrapper div
      htmlContent = htmlContent.replace(/style="[^"]*width:\s*70%[^"]*"/gi, (match) => {
        // Remove width:70% but keep other styles if any
        const cleaned = match.replace(/width:\s*70%;?\s*/gi, '');
        return cleaned === 'style=""' ? '' : cleaned;
      });

      parsedPromotionHTML.value = htmlContent;
    } else {
      parsedPromotionHTML.value = "";
    }
  } catch (error) {
    console.error("Error processing HTML:", error);
    parsedPromotionHTML.value = "";
  }
}

// ─── Helper: nested object path access ───
function getValueFromPath(obj: Record<string, any>, path: string): any {
  try {
    return path.split('.').reduce((result: any, key: string) => {
      if (result === null || result === undefined) return result;
      if (!isNaN(parseInt(key)) && Array.isArray(result)) {
        return result[parseInt(key)];
      }
      return result[key];
    }, obj);
  } catch (error) {
    return undefined;
  }
}

// ─── Data helper functions ───
function getQuotaDataGb(product: ProductType): string | number {
  const baseDataGb = getBaseQuotaDataGb(product);
  const bonusDataGb = getBonusDataGbNumber(product);
  return baseDataGb + bonusDataGb;
}

function getBaseQuotaDataGb(product: ProductType): number {
  const cf = product?.offerServiceTemplate?.[0]?.serviceTemplate?.customFields;
  return cf?.pim?.goCommerce?.quotaDataGb || 0;
}

function getBonusDataGbNumber(product: ProductType): number {
  const allowedDiscountPlans = product?.allowedDiscountPlans;
  if (!allowedDiscountPlans || !Array.isArray(allowedDiscountPlans)) return 0;
  for (const discountPlan of allowedDiscountPlans) {
    const items = Array.isArray(discountPlan.discountPlanItem)
      ? discountPlan.discountPlanItem
      : [discountPlan.discountPlanItem];
    for (const item of items) {
      if (item?.customFields?.customField && Array.isArray(item.customFields.customField)) {
        const bonusField = item.customFields.customField.find(
          (f: any) => f.code === "bonusDataAmount" && f.doubleValue
        );
        if (bonusField?.doubleValue) return Math.round(bonusField.doubleValue);
      }
    }
  }
  return 0;
}

function getBonusDataAmount(product: ProductType): string {
  const bonus = getBonusDataGbNumber(product);
  return bonus > 0 ? `${bonus}GB` : "";
}

function getFinalPrice(product: ProductType): string {
  const priceObj = getRecurringPriceObject(product);
  let price = priceObj?.price?.taxIncludedAmount || 0;
  if (priceObj?.price?.taxIncludedAmountAfterDiscount) {
    price = Number(priceObj.price.taxIncludedAmountAfterDiscount);
  }
  if (price === 0) return "$0";
  return price % 1 === 0 ? `$${price}` : formatter.currency(price);
}

function getDiscountAmount(product: ProductType): string {
  const priceObj = getRecurringPriceObject(product);
  let amount = 0;
  if (priceObj?.price?.discountAmount) {
    amount = Number(priceObj.price.discountAmount);
  } else if (priceObj?.discount?.discountValue) {
    amount = Number(priceObj.discount.discountValue);
  }
  return amount.toString();
}

function hasProductDiscount(product: ProductType): boolean {
  return Number(getDiscountAmount(product)) > 0;
}

function getOriginalPrice(product: ProductType): string {
  if (!hasProductDiscount(product)) return "";
  const fp = Number(getFinalPrice(product).replace('$', ''));
  const da = Number(getDiscountAmount(product));
  return formatter.currency(fp + da);
}

function getDiscountedPrice(product: ProductType): string {
  return hasProductDiscount(product) ? getFinalPrice(product) : "";
}

function getRppPrice(product: ProductType): string {
  const priceObj = getRecurringPriceObject(product);
  const price = priceObj?.price?.taxIncludedAmount || 0;
  return price.toString();
}

function getDurationUnit(product: ProductType): string {
  return product?.offerServiceTemplate?.[0]?.serviceTemplate?.renewalRule?.renewForUnit || "DAY";
}

function getRenewalDuration(product: ProductType): string {
  // Prefer validityDuration from customFields (accurate for 365-day plans)
  const validityDuration = product?.offerServiceTemplate?.[0]?.serviceTemplate?.customFields?.pim?.goCommerce?.validityDuration;
  if (validityDuration !== undefined && validityDuration !== null) {
    return validityDuration.toString();
  }
  // Fallback to renewalRule
  const duration = product?.offerServiceTemplate?.[0]?.serviceTemplate?.renewalRule?.renewFor;
  return duration?.toString() || "28";
}

function getPlanDurationText(product: ProductType): string {
  const duration = Number(getRenewalDuration(product));
  const unit = getDurationUnit(product);
  if (unit === "DAY") return `${duration} Day`;
  if (unit === "MONTH") return duration > 1 ? `${duration} Month` : "Month";
  if (unit === "YEAR") return duration > 1 ? `${duration} Year` : "Year";
  return "28 Day";
}

function isProductBestValue(product: ProductType): string {
  const cf = product?.offerServiceTemplate?.[0]?.serviceTemplate?.customFields;
  const isBest = cf?.pim?.goCommerce?.metadataBestValue === "true" || cf?.pim?.goCommerce?.isFeaturedProduct === true;
  return isBest ? "true" : "false";
}

function getFeatureText(product: ProductType): string {
  const cf = product?.offerServiceTemplate?.[0]?.serviceTemplate?.customFields;
  return cf?.pim?.goCommerce?.metadataFeatureText || "";
}

function getDiscountDetails(product: ProductType): any {
  if (product?.allowedDiscountPlans && Array.isArray(product.allowedDiscountPlans) && product.allowedDiscountPlans.length > 0) {
    const discountPlan = product.allowedDiscountPlans[0];
    if (discountPlan?.startDate || discountPlan?.endDate) {
      return { startDate: discountPlan.startDate, endDate: discountPlan.endDate };
    }
  }
  if (product?.productOfferingPrice?.recurring && Array.isArray(product.productOfferingPrice.recurring)) {
    const priceObj = product.productOfferingPrice.recurring.find(p => p.priceType === "recurring");
    if (priceObj?.discount) return priceObj.discount;
  }
  return { endDate: "2025-04-30T00:00:00Z", startDate: "2025-04-10T00:00:00Z" };
}

function getRecurringPriceObject(product: ProductType): any {
  if (!product?.productOfferingPrice?.recurring || !Array.isArray(product.productOfferingPrice.recurring)) return null;
  return product.productOfferingPrice.recurring.find(p => p.priceType === "recurring");
}
</script>

<template>
  <div class="product-detail-page">
    <!-- ══════════════════════════════════════════════════ -->
    <!-- 2-COLUMN LAYOUT: Left (hero+benefits) + Right (sticky OnePass card) -->
    <!-- ══════════════════════════════════════════════════ -->
    <div ref="pdpColumnsRef" class="pdp-columns">
      <!-- LEFT COLUMN -->
      <div class="pdp-left">
        <!-- Hero content -->
        <div class="hero-left">
          <!-- Promo badge -->
          <div v-if="hasPromotion" class="promo-badge">
            {{ promoBadgeText }}
          </div>

          <!-- Price -->
          <div class="price-row">
            <span class="price-amount">{{ finalPrice }}</span>
          </div>

          <!-- Data amount -->
          <div class="data-amount">{{ quotaDataGb }}GB</div>

          <!-- Ongoing recharge text (always shown for promo plans) -->
          <p v-if="hasPromotion" class="ongoing-recharge-text">
            Ongoing recharges ${{ getRppPrice(product) }} for {{ baseQuotaDataGb }}GB / {{ planDurationDays }} days
          </p>

          <!-- Plan name -->
          <div class="plan-name">{{ planDurationText }} Plan</div>

          <!-- Description -->
          <p class="plan-description">{{ planDescription }}</p>

          <!-- CTA Button -->
          <button ref="buyNowRef" class="cta-button" @click="buyNow">
            Buy now
          </button>

          <!-- CIS Link -->
          <a href="/critical-information" class="cis-link" target="_blank">
            Critical Information Summary
          </a>

          <!-- 3 Feature cards -->
          <div class="feature-cards">
            <div class="feature-card">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z" fill="#9900F1"/>
              </svg>
              <span>Fast &amp; free<br>SIM delivery</span>
            </div>
            <div class="feature-card">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z" fill="#9900F1"/>
              </svg>
              <span>Easy as mobile<br>activation</span>
            </div>
            <div class="feature-card">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C6.5 2 2 6.5 2 12C2 17.5 6.5 22 12 22C17.5 22 22 17.5 22 12C22 6.5 17.5 2 12 2ZM16.2 16.2L11 13V7H12.5V12.2L17 14.9L16.2 16.2Z" fill="#9900F1"/>
              </svg>
              <span>{{ planDurationDays }} day auto-renew<br>SIM plans</span>
            </div>
          </div>
        </div>

        <!-- Benefits (backend HTML) -->
        <div class="benefits-section" @click="handleBenefitsClick">
          <RawHtmlRenderer v-if="parsedPromotionHTML" :htmlContent="parsedPromotionHTML" />
        </div>
      </div>

      <!-- RIGHT COLUMN: Sticky OnePass promo card -->
      <div ref="pdpRightRef" class="pdp-right">
        <div class="onepass-promo-card">
          <div class="onepass-promo-content">
            <h2 class="onepass-promo-heading">
              <span class="heading-mint">FREE OnePass membership</span>
              <span class="heading-white"> when you sign up to a OnePass mobile plan</span>
            </h2>
            <a href="https://onepass.com.au/benefits" target="_blank" rel="noopener" class="onepass-promo-link">
              Learn more
            </a>
          </div>
          <!-- Mascot illustration -->
          <img
            v-if="globalSettings?.imgMascot?.url"
            :src="useStrapiMedia(globalSettings?.imgMascot?.url)"
            alt="OnePass Mascot"
            class="onepass-mascot"
          />
          <div class="onepass-promo-tcs">
            T&amp;Cs apply.
          </div>
        </div>
      </div>
    </div>

    <!-- ══════════════════════════════════════════════ -->
    <!-- SECTION 3: OnePass Perks (full width)          -->
    <!-- ══════════════════════════════════════════════ -->
    <OnePassPerks />

    <!-- ══════════════════════════════════════════════ -->
    <!-- SECTION: FAQs                                  -->
    <!-- ══════════════════════════════════════════════ -->
    <div v-if="productFaqs.length > 0" class="pdp-faqs-section">
      <h2 class="pdp-faqs-title">FAQs</h2>
      <div class="pdp-faqs-list">
        <div
          v-for="faq in productFaqs"
          :key="faq.slug"
          class="pdp-faq-item"
        >
          <button
            class="pdp-faq-header"
            @click="toggleFaq(faq.slug)"
            :aria-expanded="!!expandedFaqs[faq.slug]"
          >
            <span class="pdp-faq-headline">{{ faq.headline }}</span>
            <span class="pdp-faq-toggle">{{ expandedFaqs[faq.slug] ? '−' : '+' }}</span>
          </button>
          <Transition name="faq-expand">
            <div v-if="expandedFaqs[faq.slug]" class="pdp-faq-content" v-html="renderFaqContent(faq.content)"></div>
          </Transition>
        </div>
      </div>
    </div>

    <!-- ══════════════════════════════════════════════ -->
    <!-- MODALS (Teleport to body)                     -->
    <!-- ══════════════════════════════════════════════ -->
    <Teleport to="body">
      <!-- OnePass modal -->
      <div v-if="showOpModal" class="detail-modal-overlay" @click.self="showOpModal = false">
        <div class="detail-modal">
          <div class="detail-modal-header">
            <div class="detail-modal-header-left">
              <img :src="'/api/strapi/media/uploads/One_Pass_logo_b7270f1a3a.svg'" alt="OnePass" class="detail-modal-icon" />
              <span class="detail-modal-title" style="color: #3A016A;">OnePass</span>
            </div>
            <button class="detail-modal-close" @click="showOpModal = false" aria-label="Close">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#3A016A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
          <div class="detail-modal-body">
            {{ opModalDescription || 'Save money shopping online, multiply your points faster and get free delivery on eligible items as a OnePass member. Eligibility criteria and T&Cs apply.' }}
          </div>
        </div>
      </div>

      <!-- Flybuys modal -->
      <div v-if="showFlybuysModal" class="detail-modal-overlay" @click.self="showFlybuysModal = false">
        <div class="detail-modal">
          <div class="detail-modal-header">
            <div class="detail-modal-header-left">
              <img :src="'/api/strapi/media/uploads/Frame_1597882200_44d4c86e42.svg'" alt="Flybuys" class="detail-modal-icon" />
              <span class="detail-modal-title" style="color: #054DFF;">Flybuys</span>
            </div>
            <button class="detail-modal-close" @click="showFlybuysModal = false" aria-label="Close">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#3A016A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
          <div class="detail-modal-body">
            {{ flybuysModalDescription || 'Customers on eligible OnePass Mobile plans can earn 5x Flybuys points for every $1 spent on recharge, when their Flybuys account is linked to their OnePass membership. Points are awarded on eligible recharge transactions made while your plan and membership remain active. Eligibility criteria and T&Cs apply.' }}
          </div>
        </div>
      </div>

      <!-- Speed modal -->
      <div v-if="showSpeedModal" class="detail-modal-overlay" @click.self="showSpeedModal = false">
        <div class="detail-modal">
          <div class="detail-modal-header">
            <div class="detail-modal-header-left">
              <img :src="'/api/strapi/media/uploads/network_tower_97ce8beed7.svg'" alt="Network" class="detail-modal-icon" />
              <span class="detail-modal-title" style="color: #3A016A;">Network Speed</span>
            </div>
            <button class="detail-modal-close" @click="showSpeedModal = false" aria-label="Close">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#3A016A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
          <div class="detail-modal-body">
            {{ speedModalDescription || 'Enjoy more reliable connections on compatible devices. Typical download speeds range from 50-150 Mbps on 4G and up to 300 Mbps on 5G, depending on location, device, and network conditions. T&Cs apply.' }}
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ══════════════════════════════════════════════ -->
    <!-- STICKY BOTTOM BAR (desktop only)              -->
    <!-- ══════════════════════════════════════════════ -->
    <Teleport to="body">
      <Transition name="sticky-slide">
        <div v-if="showStickyBar" class="sticky-bottom-bar">
          <div class="sticky-bottom-inner">
            <div class="sticky-bottom-info">
              <span class="sticky-bottom-data">{{ quotaDataGb }}GB for {{ finalPrice }}</span>
              <span class="sticky-bottom-duration">{{ planDurationText }} plan</span>
            </div>
            <button class="sticky-bottom-cta" @click="buyNow">
              Buy now
            </button>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped lang="scss">
/* ═══════════════════════════════════════════════════════ */
/* Design tokens from Figma                              */
/* ═══════════════════════════════════════════════════════ */
$purple: #9900F1;
$royal: #3A016A;
$mint: #B0F4DE;
$wisteria: #F4E7FF;
$black: #333333;
$white: #FFFFFF;
$flybuys-blue: #054DFF;
$border-light: #EEEEE9;
$divider: #F4E7FF;

.product-detail-page {
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 40px;
  font-family: 'Montserrat', sans-serif;
}

/* ═══════════════════════════════════════════════════════ */
/* 2-COLUMN LAYOUT: left (hero+benefits) + right (sticky)*/
/* ═══════════════════════════════════════════════════════ */
.pdp-columns {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  gap: 4em;
  padding: 4em 0;
}

.pdp-left {
  flex: 1 1 0;
  max-width: 50%;
  display: flex;
  flex-direction: column;
}

.pdp-right {
  flex: 0 0 32%;
  align-self: flex-start;
  will-change: transform;
}

.hero-left {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1em;
}

.promo-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.25em 0.5em;
  background: $mint;
  border-radius: 0.5em;
  font-weight: 600;
  font-size: 0.875rem;
  line-height: 1.375rem;
  color: $black;
}

.price-row {
  display: flex;
  align-items: baseline;
  gap: 0.75em;
  margin-bottom: -1em;
}

.price-amount {
  font-weight: 700;
  font-size: clamp(2.5rem, 4vw, 3.5rem);
  line-height: 1.2;
  letter-spacing: -0.01em;
  color: $purple;
}

.data-amount {
  font-weight: 500;
  font-size: clamp(2.5rem, 4vw, 3.5rem);
  line-height: 1.2;
  letter-spacing: -0.01em;
  color: $royal;
}

.plan-name {
  font-weight: 700;
  font-size: 1.25rem;
  line-height: 1.75rem;
  color: $royal;
  padding-bottom: 1rem;
}

.cis-link {
  display: inline-block;
  font-weight: 400;
  font-size: 0.875rem;
  line-height: 1.375rem;
  color: $purple;
  text-decoration: underline;
  text-underline-offset: 2px;
  text-align: center;
  width: 100%;

  &:hover {
    opacity: 0.8;
  }
}

.ongoing-recharge-text {
  font-weight: 400;
  font-size: 1rem;
  line-height: 1.5rem;
  color: $royal;
  margin: 0;
}

.plan-description {
  font-weight: 400;
  font-size: 1.125rem;
  line-height: 1.625rem;
  color: $royal;
  margin: 0;
  padding-bottom: 1rem;
}

.cta-button {
  width: 100%;
  padding: 1em 2em;
  display: flex;
  align-items: center;
  justify-content: center;
  background: $purple;
  border: none;
  border-radius: 100px;
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  font-size: 1rem;
  line-height: 1.5rem;
  color: $white;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
}

/* 3 Feature cards */
.feature-cards {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 1.5em;
  width: 100%;
}

.feature-card {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5em;
  text-align: center;

  svg {
    flex-shrink: 0;
  }

  span {
    font-weight: 400;
    font-size: 1rem;
    line-height: 1.5rem;
    color: $royal;
  }
}


/* OnePass promo card — content-driven height, mascot overflows bottom */
.onepass-promo-card {
  width: 100%;
  background: $purple;
  border-radius: 1em;
  padding: 1.5em;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 1.5em;
  position: relative;
  padding-bottom: 35%; /* space for mascot at bottom */
}

.onepass-promo-content {
  display: flex;
  flex-direction: column;
  gap: 1.25em;
  z-index: 2;
}

.onepass-promo-heading {
  font-weight: 700;
  font-size: clamp(1.5rem, 3vw, 2.5rem);
  line-height: 1.2;
  letter-spacing: -0.01em;
  margin: 0;

  .heading-mint {
    color: $mint;
  }

  .heading-white {
    color: $white;
  }
}

.onepass-promo-link {
  display: inline-flex;
  align-items: center;
  font-weight: 600;
  font-size: 1rem;
  line-height: 1.5rem;
  color: $white;
  text-decoration: underline;
  text-underline-offset: 2px;
  width: fit-content;

  &:hover {
    opacity: 0.9;
  }
}

/* Mascot — positioned at bottom-right, overflows card */
.onepass-mascot {
  position: absolute;
  right: 0;
  bottom: 0;
  transform: translateY(45%);
  width: 54%; /* 250/465 ≈ 54% of card width */
  height: auto;
  object-fit: contain;
  z-index: 1;
  pointer-events: none;
}

.onepass-promo-tcs {
  position: absolute;
  bottom: 1.5em;
  left: 1.5em;
  font-weight: 400;
  font-size: 0.875rem;
  line-height: 1.25rem;
  letter-spacing: -0.01em;
  color: $white;
  text-align: left;
  z-index: 2;
}

/* ═══════════════════════════════════════════════════════ */
/* BENEFITS SECTION                                      */
/* ═══════════════════════════════════════════════════════ */
.benefits-section {
  padding-top: 1.5em;
  padding-bottom: 4em;
  border-top: 1px solid $divider;
}

/* ═══════════════════════════════════════════════════════ */
/* MODALS                                                */
/* ═══════════════════════════════════════════════════════ */
.detail-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #3A016A33;
}

.detail-modal {
  width: min(652px, 90vw);
  background: $white;
  border: 1px solid $border-light;
  border-radius: 0.625em;
  overflow: hidden;
}

.detail-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25em 1.5em;
  border-bottom: 1px solid $border-light;
}

.detail-modal-header-left {
  display: flex;
  align-items: center;
  gap: 0.625em;
}

.detail-modal-icon {
  width: 1.75em;
  height: 1.75em;
  object-fit: contain;
}

.detail-modal-title {
  font-weight: 700;
  font-size: 1.25rem;
  line-height: 1.75rem;
}

.detail-modal-close {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25em;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    opacity: 0.7;
  }
}

.detail-modal-body {
  padding: 1.5em;
  font-weight: 400;
  font-size: 1rem;
  line-height: 1.5rem;
  color: $royal;
  white-space: pre-line;
}

/* ═══════════════════════════════════════════════════════ */
/* STICKY BOTTOM BAR (desktop only)                      */
/* ═══════════════════════════════════════════════════════ */
.sticky-bottom-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: $royal;
}

.sticky-bottom-inner {
  max-width: 1280px;
  margin: 0 auto;
  padding: 16px 80px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.sticky-bottom-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.sticky-bottom-data {
  font-weight: 700;
  font-size: 1.125rem;
  line-height: 1.5rem;
  color: $white;
}

.sticky-bottom-duration {
  font-weight: 400;
  font-size: 1rem;
  line-height: 1.5rem;
  color: $white;
  opacity: 0.8;
}

.sticky-bottom-cta {
  padding: 8px 32px;
  background: $white;
  border: none;
  border-radius: 100px;
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  font-size: 0.875rem;
  line-height: 1.375rem;
  color: $royal;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
}

/* Slide transition */
.sticky-slide-enter-active,
.sticky-slide-leave-active {
  transition: transform 0.3s ease;
}

.sticky-slide-enter-from,
.sticky-slide-leave-to {
  transform: translateY(100%);
}

/* ═══════════════════════════════════════════════════════ */
/* TABLET (≤1024px) — squeeze ratios gracefully          */
/* ═══════════════════════════════════════════════════════ */
@media (max-width: 1280px) {
  .product-detail-page {
    padding: 0 40px;
  }

  .sticky-bottom-inner {
    padding: 16px 40px;
  }
}

@media (max-width: 1024px) {
  .product-detail-page {
    padding: 0 24px;
  }

  .pdp-columns {
    gap: 2em;
    padding: 3em 0;
  }

  .pdp-left {
    max-width: 55%;
  }

  .pdp-right {
    flex: 0 0 40%;
  }

  .sticky-bottom-inner {
    padding: 16px 24px;
  }
}

/* ═══════════════════════════════════════════════════════ */
/* MOBILE (≤768px) — single column                       */
/* ═══════════════════════════════════════════════════════ */
@media (max-width: 768px) {
  .product-detail-page {
    padding: 0 16px;
  }

  .pdp-columns {
    flex-direction: column;
    gap: 2em;
    padding: 2em 0;
  }

  .pdp-left {
    max-width: 100%;
  }

  .pdp-right {
    position: static;
    flex: none;
    width: 100%;
  }

  .hero-left {
    gap: 1.5em;
  }

  .data-amount {
    font-weight: 600;
  }

  .cta-button {
    border-radius: 2.5em;
    font-weight: 700;
    font-size: 1.125rem;
  }

  .feature-cards {
    gap: 1.25em;
  }

  .benefits-section {
    max-width: 100%;
    padding: 1.5em 0 4em;
  }

  .onepass-promo-card {
    padding-bottom: 40%;
  }

  .onepass-mascot {
    width: 45%;
  }

  /* Hide sticky bottom bar on mobile */
  .sticky-bottom-bar {
    display: none;
  }
}

/* ═══════════════════════════════════════════════════════ */
/* FAQs SECTION                                          */
/* ═══════════════════════════════════════════════════════ */
.pdp-faqs-section {
  padding: 3em 0;
}

.pdp-faqs-title {
  font-weight: 700;
  font-size: 1.125rem;
  line-height: 1.5rem;
  color: $royal;
  margin: 0 0 1.5em 0;
  text-align: center;
}

.pdp-faqs-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.pdp-faq-item {
  border: 1px solid $border-light;
  border-radius: 12px;
  overflow: hidden;
}

.pdp-faq-header {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: none;
  border: none;
  cursor: pointer;
  font-family: 'Montserrat', sans-serif;
  text-align: left;

  &:hover {
    background: #fafafa;
  }
}

.pdp-faq-headline {
  font-weight: 700;
  font-size: 1rem;
  line-height: 1.5rem;
  color: $black;
}

.pdp-faq-toggle {
  flex-shrink: 0;
  font-size: 1.25rem;
  font-weight: 300;
  color: #333;
  line-height: 1;
}

.pdp-faq-content {
  padding: 0 20px 20px;
  font-family: 'Montserrat', sans-serif;
  font-weight: 400;
  font-size: 0.875rem;
  line-height: 1.5;
  color: $black;

  :deep(p) {
    margin: 0 0 0.75em;
  }

  :deep(p:last-child) {
    margin-bottom: 0;
  }

  :deep(a) {
    color: $purple;
    text-decoration: underline;
  }

  :deep(strong) {
    font-weight: 700;
  }

  :deep(ol) {
    padding-left: 1.25em;
    margin: 0.5em 0;
  }

  :deep(li) {
    margin-bottom: 0.25em;
  }
}

/* FAQ expand transition */
.faq-expand-enter-active,
.faq-expand-leave-active {
  transition: all 0.2s ease;
  overflow: hidden;
}

.faq-expand-enter-from,
.faq-expand-leave-to {
  opacity: 0;
  max-height: 0;
  padding-top: 0;
  padding-bottom: 0;
}
</style>
